// SA-3
import { useCallback, useEffect, useMemo, useState } from 'react'

import { COL_PRODUCTION_CALENDAR_DAYS, COL_SHIFTS } from '../../../config/collections'
import { PERMISSION_KEYS } from '../../../config/permissions'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import {
  MONTH_LABELS,
  WEEKDAY_LABELS,
  calendarDayDetailLabel,
  daysInMonth,
  defaultIsWorking,
  normalizePbDate,
  parseDateKey,
  calendarDateRangeFilter,
  normalizeRelationId,
  startOfMonth,
  toDateKey,
  weekdayMon0,
} from '../../../lib/productionCalendar'
import { brigadeLabel } from '../../../lib/shiftRotation'
import { isRotationShift } from '../../../lib/shifts'
import { pb } from '../../../pocketbase'
import { useAuth } from '../../../context/AuthContext'
import type { ProductionCalendarDayRecord, ShiftRecord } from '../../../types/records'
import { Alert, FormField, PageHeader, PageShell, formInputProps } from '../../../components/ui'
import { Modal, ModalActions, modalBtn } from '../../../components/ui/Modal'
import { btn, cn } from '../../../lib/ui'

type DayCell = {
  dateKey: string
  day: number
  inMonth: boolean
}

type DayState = {
  recordId?: string
  isWorking: boolean
  workingBrigade: string
  note: string
  hasRecord: boolean
}

function buildMonthGrid(year: number, month: number): DayCell[] {
  const first = startOfMonth(year, month)
  const leading = weekdayMon0(first)
  const totalDays = daysInMonth(year, month)
  const cells: DayCell[] = []

  for (let i = 0; i < leading; i++) {
    const d = new Date(year, month, -leading + i + 1)
    cells.push({ dateKey: toDateKey(d), day: d.getDate(), inMonth: false })
  }
  for (let day = 1; day <= totalDays; day++) {
    const d = new Date(year, month, day)
    cells.push({ dateKey: toDateKey(d), day, inMonth: true })
  }
  while (cells.length % 7 !== 0) {
    const last = parseDateKey(cells[cells.length - 1].dateKey)
    const d = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1)
    cells.push({ dateKey: toDateKey(d), day: d.getDate(), inMonth: false })
  }
  return cells
}

export function ProductionCalendarPage() {
  const { can } = useAuth()
  const canWrite = can(PERMISSION_KEYS.PRODUCTION_CALENDAR_WRITE)
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth())
  const [records, setRecords] = useState<ProductionCalendarDayRecord[]>([])
  const [rotationShifts, setRotationShifts] = useState<ShiftRecord[]>([])
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [editKey, setEditKey] = useState<string | null>(null)
  const [editWorking, setEditWorking] = useState(true)
  const [editBrigade, setEditBrigade] = useState('')
  const [editNote, setEditNote] = useState('')

  const monthStart = toDateKey(startOfMonth(year, month))
  const monthEnd = toDateKey(new Date(year, month, daysInMonth(year, month)))

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const [list, shiftList] = await Promise.all([
        pb.collection(COL_PRODUCTION_CALENDAR_DAYS).getFullList<ProductionCalendarDayRecord>({
          filter: calendarDateRangeFilter(monthStart, monthEnd),
          sort: 'date',
          expand: 'workingBrigade',
        }),
        pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({ sort: 'sort', filter: 'active = true' }),
      ])
      setRecords(list)
      setRotationShifts(shiftList.filter((s) => isRotationShift(s)))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setRecords([])
    } finally {
      setLoading(false)
    }
  }, [monthStart, monthEnd])

  useEffect(() => {
    void load()
  }, [load])

  const recordByDate = useMemo(() => {
    const map = new Map<string, ProductionCalendarDayRecord>()
    for (const r of records) {
      map.set(normalizePbDate(r.date), r)
    }
    return map
  }, [records])

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month])

  const shiftsById = useMemo(() => new Map(rotationShifts.map((s) => [s.id, s])), [rotationShifts])

  const calendarBrigadeMap = useMemo(() => {
    const map = new Map<string, Pick<ProductionCalendarDayRecord, 'isWorking' | 'workingBrigade'>>()
    for (const r of records) {
      map.set(normalizePbDate(r.date), {
        isWorking: r.isWorking,
        workingBrigade: normalizeRelationId(r.workingBrigade),
      })
    }
    return map
  }, [records])

  function dayState(dateKey: string): DayState {
    const rec = recordByDate.get(dateKey)
    if (rec) {
      return {
        recordId: rec.id,
        isWorking: rec.isWorking,
        workingBrigade: normalizeRelationId(rec.workingBrigade),
        note: rec.note || '',
        hasRecord: true,
      }
    }
    return {
      isWorking: defaultIsWorking(parseDateKey(dateKey)),
      workingBrigade: '',
      note: '',
      hasRecord: false,
    }
  }

  function openDay(dateKey: string) {
    if (!canWrite) return
    const st = dayState(dateKey)
    setEditKey(dateKey)
    setEditWorking(st.isWorking)
    setEditBrigade(st.workingBrigade)
    setEditNote(st.note)
  }

  function prevMonth() {
    if (month === 0) {
      setYear((y) => y - 1)
      setMonth(11)
    } else {
      setMonth((m) => m - 1)
    }
  }

  function nextMonth() {
    if (month === 11) {
      setYear((y) => y + 1)
      setMonth(0)
    } else {
      setMonth((m) => m + 1)
    }
  }

  async function saveDay(e: React.FormEvent) {
    e.preventDefault()
    if (!editKey) return
    setSaving(true)
    setErr('')
    try {
      const st = dayState(editKey)
      const body = {
        date: editKey,
        isWorking: editWorking,
        workingBrigade: editWorking ? editBrigade : '',
        note: editNote.trim(),
      }
      if (st.recordId) {
        await pb.collection(COL_PRODUCTION_CALENDAR_DAYS).update(st.recordId, body)
      } else {
        await pb.collection(COL_PRODUCTION_CALENDAR_DAYS).create(body)
      }
      setEditKey(null)
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    } finally {
      setSaving(false)
    }
  }

  const editLabel = editKey
    ? parseDateKey(editKey).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : ''

  return (
    <PageShell>
      <PageHeader
        title="Производственный календарь"
        lead="Рабочие дни цеха и рабочая бригада 2/2 (Смена 1 / Смена 2) на каждый день. По умолчанию все дни рабочие; отмечайте простои и указывайте, какая бригада вышла."
      />
      {err && <Alert kind="err">{err}</Alert>}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button type="button" className={btn()} onClick={prevMonth} disabled={loading || saving}>
          ←
        </button>
        <span className="min-w-40 text-center text-base font-semibold text-su-text-strong">
          {MONTH_LABELS[month]} {year}
        </span>
        <button type="button" className={btn()} onClick={nextMonth} disabled={loading || saving}>
          →
        </button>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading || saving}>
          Обновить
        </button>
        {canWrite && (
          <button
            type="button"
            className={btn()}
            onClick={() => {
              setEditKey(toDateKey(today))
              setEditWorking(false)
              setEditBrigade('')
              setEditNote('')
            }}
            disabled={loading || saving}
          >
            Простой сегодня
          </button>
        )}
      </div>

      <div className="overflow-auto rounded-su-lg border border-su-border bg-su-surface shadow-su-md">
        <div className="grid grid-cols-7 border-b border-su-border bg-su-surface-muted text-center text-xs font-semibold text-slate-500">
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className="border-r border-su-border px-2 py-2 last:border-r-0">
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((cell) => {
            const st = dayState(cell.dateKey)
            const brigadeText = cell.inMonth
              ? brigadeLabel(cell.dateKey, calendarBrigadeMap, shiftsById)
              : ''
            const isToday = cell.dateKey === toDateKey(today)
            return (
              <button
                key={cell.dateKey}
                type="button"
                disabled={!cell.inMonth || !canWrite || loading || saving}
                onClick={() => openDay(cell.dateKey)}
                className={cn(
                  'flex min-h-[4.5rem] flex-col items-start border-b border-r border-su-border p-2 text-left text-sm transition last:border-r-0',
                  !cell.inMonth && 'bg-su-surface-muted/40 text-slate-400',
                  cell.inMonth && st.isWorking && 'bg-emerald-50/50 hover:bg-emerald-50 dark:bg-emerald-950/20',
                  cell.inMonth && !st.isWorking && 'bg-red-50/50 hover:bg-red-50 dark:bg-red-950/20',
                  cell.inMonth && canWrite && 'cursor-pointer',
                  isToday && cell.inMonth && 'ring-2 ring-inset ring-su-primary/50',
                )}
              >
                <span className="font-semibold">{cell.day}</span>
                {cell.inMonth && (
                  <>
                    <span className="mt-0.5 text-[0.7rem] text-su-text">
                      {calendarDayDetailLabel(st.isWorking, cell.dateKey)}
                      {!st.hasRecord && ' *'}
                    </span>
                    <span className="mt-0.5 text-[0.65rem] font-medium text-indigo-700 dark:text-indigo-300">
                      {brigadeText}
                    </span>
                    {st.note && (
                      <span className="mt-0.5 line-clamp-2 text-[0.65rem] text-slate-500">{st.note}</span>
                    )}
                  </>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <p className="mt-3 text-xs text-su-text">
        * — день без явной записи; по умолчанию рабочий (в т.ч. сб/вс). Укажите бригаду 2/2 для табеля и графика.
        {!canWrite && ' Редактирование недоступно — требуется право на изменение календаря.'}
      </p>

      {editKey && (
        <Modal title={`День: ${editLabel}`} onClose={() => setEditKey(null)}>
          <form onSubmit={(e) => void saveDay(e)} className="flex flex-col gap-4">
            <FormField label="Тип дня" htmlFor="cal-working">
              <label className="flex items-center gap-2">
                <input
                  id="cal-working"
                  type="checkbox"
                  checked={editWorking}
                  onChange={(e) => {
                    setEditWorking(e.target.checked)
                    if (!e.target.checked) setEditBrigade('')
                  }}
                />
                <span>{editWorking ? 'Рабочий день цеха' : 'Простой / праздник'}</span>
              </label>
            </FormField>
            <FormField label="Рабочая бригада 2/2" htmlFor="cal-brigade">
              <select
                id="cal-brigade"
                value={editBrigade}
                onChange={(e) => setEditBrigade(e.target.value)}
                disabled={!editWorking}
                {...formInputProps()}
              >
                <option value="">— не задано —</option>
                {rotationShifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.startTime}–{s.endTime})
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Примечание" htmlFor="cal-note">
              <input
                id="cal-note"
                type="text"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Праздник, ремонт цеха…"
                maxLength={500}
                {...formInputProps()}
              />
            </FormField>
            <ModalActions>
              <button type="submit" className={modalBtn('primary')} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
              <button type="button" className={modalBtn('ghost')} onClick={() => setEditKey(null)}>
                Отмена
              </button>
            </ModalActions>
          </form>
        </Modal>
      )}
    </PageShell>
  )
}
